import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { api } from "@/lib/api";

/* ============================================================================
   BB-067A: Elvis Aloha Archive — PRODUCTION HOMEPAGE
   
   APPROVED HERO: Candidate B - Ticket Collection Focus
   Approved: July 4, 2025
   
   Two-image editorial layout emphasizing:
   - Authentic ticket collection
   - Globe portrait design detail
   - Scale and historical importance
   
   All text is real HTML. No embedded image text.
   ============================================================================ */

// Collection imagery paths (authenticated Graceland photos, September 14, 2022)
const COLLECTION_IMAGES = {
  // Hero - Candidate B (Two-image editorial layout)
  heroPrimary: "/assets/collection/real-graceland-costume-tickets-2.jpg",
  heroSecondary: "/assets/collection/real-proof-globe-portrait-1.jpg",
  heroPrimaryAlt: "Original Aloha from Hawaii ticket collection showing the scale and variety of preserved concert tickets",
  heroSecondaryAlt: "Globe Tickets portrait design detail from the original production proofs",
  
  // Collection Highlights Section
  documents: "/assets/collection/real-graceland-documents-1.jpg",
  artworkProofs: "/assets/collection/real-proof-artwork-grid.jpg",
  ticketDetail: "/assets/collection/real-graceland-costume-tickets-1.jpg",
  
  // Additional Graceland display
  gracelandDisplay: "/assets/collection/real-graceland-framed-tickets-1.jpg"
};

/* ============================================================================
   PRODUCTION HERO: Candidate B - Ticket Collection Focus
   Single image: The 50 tickets with Adam standing in front
   ============================================================================ */
const ProductionHero = () => (
  <div style={{ 
    minWidth: 0,
    width: '100%',
    maxWidth: '700px',
    justifySelf: 'end'
  }}>
    {/* Museum-style mount */}
    <div style={{
      background: 'rgba(255, 252, 245, 0.88)',
      border: '1px solid rgba(181, 138, 60, 0.30)',
      padding: 'clamp(8px, 2vw, 20px)',
      borderRadius: '4px'
    }}>
      <img 
        src={COLLECTION_IMAGES.heroPrimary}
        alt={COLLECTION_IMAGES.heroPrimaryAlt}
        style={{
          display: 'block',
          width: '100%',
          height: 'auto',
          maxHeight: '520px',
          objectFit: 'cover',
          objectPosition: 'center',
          borderRadius: '2px'
        }}
        loading="eager"
        data-testid="hero-image"
      />
      {/* Caption - Required exact wording */}
      <p style={{
        marginTop: '14px',
        fontFamily: "'Source Sans 3', sans-serif",
        fontSize: 'clamp(13px, 1.8vw, 15px)',
        lineHeight: 1.45,
        color: '#5A6B70',
        textAlign: 'center',
        letterSpacing: '0.01em'
      }}
      data-testid="hero-caption"
      >
        Documented display at Graceland on September 14, 2022.
      </p>
    </div>
  </div>
);

/* ============================================================================
   HERO TEXT CONTENT
   Required HTML text - not embedded in images
   ============================================================================ */
const HeroTextContent = () => (
  <div style={{ minWidth: 0, maxWidth: '620px' }}>
    {/* Eyebrow - AA compliant gold (#8A6928 = 4.52:1 contrast) */}
    <p 
      style={{
        marginBottom: '16px',
        color: '#8A6928',
        fontFamily: "'Source Sans 3', sans-serif",
        fontSize: 'clamp(12px, 2vw, 15px)',
        fontWeight: 700,
        letterSpacing: '0.16em',
        textTransform: 'uppercase'
      }}
      data-testid="hero-eyebrow"
    >
      An Independent Historical Collection
    </p>
    
    {/* H1 - Real HTML */}
    <h1 
      style={{
        margin: 0,
        color: '#0D273C',
        fontFamily: "'Cormorant Garamond', serif",
        fontSize: 'clamp(32px, 7vw, 68px)',
        lineHeight: 1.05,
        fontWeight: 600,
        letterSpacing: '-0.02em'
      }}
      data-testid="hero-h1"
    >
      The Collection Behind Aloha from Hawaii
    </h1>
    
    {/* Supporting Copy */}
    <p 
      style={{
        marginTop: 'clamp(16px, 3vw, 28px)',
        maxWidth: '580px',
        color: '#3D403F',
        fontFamily: "'Source Serif 4', 'Source Sans 3', serif",
        fontSize: 'clamp(15px, 2.2vw, 19px)',
        lineHeight: 1.6
      }}
      data-testid="hero-description"
    >
      Explore original tickets, production materials, photographs, documents, 
      and research preserving the documented history of Elvis Presley's 1973 
      Aloha from Hawaii event.
    </p>
    
    {/* CTA Buttons - Stack on mobile */}
    <div 
      className="hero-cta-group"
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '12px',
        marginTop: 'clamp(20px, 4vw, 32px)'
      }}
    >
      <style>{`
        @media (max-width: 640px) {
          .hero-cta-group { 
            flex-direction: column; 
            align-items: stretch; 
          }
          .hero-cta-group a { 
            width: 100%; 
            max-width: 280px;
            min-width: 0 !important;
          }
        }
      `}</style>
      <Link 
        to="/collection"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '48px',
          minWidth: '200px',
          padding: '12px 28px',
          borderRadius: '4px',
          backgroundColor: '#D85C4A',
          color: '#FFFFFF',
          border: 'none',
          fontFamily: "'Source Sans 3', sans-serif",
          fontSize: '14px',
          fontWeight: 600,
          letterSpacing: '0.03em',
          textTransform: 'uppercase',
          textDecoration: 'none',
          textAlign: 'center'
        }}
        data-testid="hero-cta-collection"
      >
        Explore the Collection
      </Link>
      <Link 
        to="/arena"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '48px',
          minWidth: '200px',
          padding: '12px 28px',
          borderRadius: '4px',
          backgroundColor: 'rgba(247, 241, 229, 0.92)',
          color: '#0D273C',
          border: '1px solid #D2C19F',
          fontFamily: "'Source Sans 3', sans-serif",
          fontSize: '14px',
          fontWeight: 600,
          letterSpacing: '0.03em',
          textTransform: 'uppercase',
          textDecoration: 'none',
          textAlign: 'center'
        }}
        data-testid="hero-cta-arena"
      >
        Explore the Arena
      </Link>
    </div>
    
    {/* Optional text link - with proper touch target */}
    <div style={{ marginTop: '12px' }}>
      <Link 
        to="/studies"
        style={{
          fontFamily: "'Source Sans 3', sans-serif",
          fontSize: '14px',
          fontWeight: 600,
          color: '#598085',
          textDecoration: 'none',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          minHeight: '44px',
          paddingBlock: '8px',
          paddingInline: '4px'
        }}
        data-testid="hero-link-research"
      >
        View the Research <ArrowRight style={{ width: '14px', height: '14px' }} />
      </Link>
    </div>
  </div>
);

/* ============================================================================
   MAIN HOMEPAGE COMPONENT
   ============================================================================ */
const HomePage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/collection/items")
      .then((res) => setItems(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const featured = useMemo(() => {
    const f = items.filter((i) => i.featured || i.graceland_displayed);
    return f.length > 0 ? f[0] : items[0];
  }, [items]);

  return (
    <div data-testid="home-page" style={{ overflow: 'hidden' }}>
      
      {/* ========== HERO SECTION — CANDIDATE B: TICKET COLLECTION FOCUS ========== */}
      <section 
        style={{
          position: 'relative',
          minHeight: 'auto',
          backgroundColor: '#F7F1E5',
          padding: 'clamp(32px, 6vw, 72px) clamp(16px, 4vw, 48px) clamp(24px, 4vw, 48px)'
        }}
        data-testid="hero-section"
      >
        <div 
          style={{
            maxWidth: '1440px',
            margin: '0 auto'
          }}
        >
          {/* Responsive grid - stacks on mobile */}
          <div 
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 380px), 1fr))',
              gap: 'clamp(24px, 4vw, 64px)',
              alignItems: 'center'
            }}
          >
            {/* Text Content */}
            <HeroTextContent />
            
            {/* Production Hero - Candidate B */}
            <ProductionHero />
          </div>
        </div>
      </section>

      {/* ========== HISTORICAL INTRODUCTION ========== */}
      <section 
        style={{ 
          backgroundColor: '#F7F1E5',
          padding: 'clamp(24px, 4vw, 48px) clamp(16px, 4vw, 48px)',
          textAlign: 'center'
        }}
        data-testid="intro-section"
      >
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          <h2 style={{
            fontFamily: "'Cormorant Garamond', serif",
            fontSize: 'clamp(28px, 6vw, 42px)',
            color: '#0D273C',
            marginBottom: 'clamp(16px, 3vw, 24px)',
            fontWeight: 400,
            lineHeight: 1.1
          }}>
            January 14, 1973: A Documented Chronicle
          </h2>
          
          <div style={{ 
            fontFamily: "'Source Serif 4', 'Source Sans 3', serif", 
            fontSize: 'clamp(15px, 2.5vw, 18px)', 
            lineHeight: 1.7, 
            color: '#3D403F' 
          }}>
            <p style={{ marginBottom: '16px' }}>
              The Elvis Aloha Archive preserves original artifacts, production materials, and 
              contemporaneous newspaper sources documenting Elvis Presley's Aloha from Hawaii 
              satellite concert. The January 14, 1973 program was transmitted live to 
              participating markets in Asia and Oceania. The United States broadcast followed 
              on April 4, 1973, while scheduling in other territories varied.
            </p>
            <p>
              This independent archive distinguishes documented fact from unverified repetition, 
              classifying each claim by primary source evidence and acknowledging open questions 
              rather than filling gaps with speculation.
            </p>
          </div>
        </div>
      </section>

      {/* ========== ARCHIVE CATEGORIES — RESPONSIVE GRID ========== */}
      <section 
        style={{ 
          backgroundColor: '#F7F1E5',
          padding: 'clamp(24px, 4vw, 48px) clamp(16px, 4vw, 48px)'
        }}
        data-testid="categories-section"
      >
        <style>{`
          @media (max-width: 599px) {
            .category-grid { grid-template-columns: repeat(2, 1fr) !important; }
          }
          @media (min-width: 600px) and (max-width: 899px) {
            .category-grid { grid-template-columns: repeat(3, 1fr) !important; }
          }
          @media (min-width: 900px) {
            .category-grid { grid-template-columns: repeat(6, 1fr) !important; }
          }
        `}</style>
        
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div 
            className="category-grid"
            style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: 'clamp(8px, 2vw, 16px)'
            }}
          >
            {[
              { id: 'tickets', label: 'Tickets', action: 'Browse', link: '/collection?category=tickets', iconType: 'ticket' },
              { id: 'artifacts', label: 'Artifacts', action: 'Explore', link: '/collection?category=artifacts', iconType: 'package' },
              { id: 'documents', label: 'Documents', action: 'View', link: '/collection?category=documents', iconType: 'file-text' },
              { id: 'photographs', label: 'Photos', action: 'Gallery', link: '/collection?category=photographs', iconType: 'camera' },
              { id: 'recordings', label: 'Recordings', action: 'Listen', link: '/collection?category=recordings', iconType: 'music' },
              { id: 'memorabilia', label: 'Memorabilia', action: 'Discover', link: '/collection?category=memorabilia', iconType: 'bookmark' },
            ].map((cat) => {
              // Icon mapping using inline SVGs for consistent styling
              const iconMap = {
                'ticket': <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z"/><path d="M13 5v2"/><path d="M13 17v2"/><path d="M13 11v2"/></svg>,
                'package': <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m7.5 4.27 9 5.15"/><path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z"/><path d="m3.3 7 8.7 5 8.7-5"/><path d="M12 22V12"/></svg>,
                'file-text': <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><line x1="10" y1="9" x2="8" y2="9"/></svg>,
                'camera': <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z"/><circle cx="12" cy="13" r="3"/></svg>,
                'music': <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/></svg>,
                'bookmark': <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m19 21-7-4-7 4V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16z"/></svg>
              };
              return (
              <Link 
                key={cat.id}
                to={cat.link}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  backgroundColor: '#FBF7EF',
                  border: '1px solid rgba(181, 138, 60, 0.32)',
                  borderRadius: '6px',
                  padding: 'clamp(12px, 2vw, 24px) clamp(8px, 1.5vw, 16px)',
                  minHeight: 'clamp(80px, 15vw, 130px)',
                  textDecoration: 'none'
                }}
                data-testid={`category-${cat.id}`}
              >
                <div style={{ 
                  width: 'clamp(32px, 6vw, 44px)', 
                  height: 'clamp(32px, 6vw, 44px)', 
                  backgroundColor: '#EEE3D0', 
                  borderRadius: '8px', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  marginBottom: '8px',
                  color: '#0D273C'
                }}>
                  {iconMap[cat.iconType]}
                </div>
                <h3 style={{ 
                  fontFamily: "'Source Sans 3', sans-serif", 
                  fontSize: 'clamp(11px, 2vw, 13px)', 
                  fontWeight: 700, 
                  color: '#0D273C', 
                  textTransform: 'uppercase', 
                  letterSpacing: '0.04em',
                  marginBottom: '2px'
                }}>
                  {cat.label}
                </h3>
                <span style={{ 
                  fontFamily: "'Source Sans 3', sans-serif", 
                  fontSize: 'clamp(10px, 1.5vw, 11px)', 
                  color: '#6B7280' 
                }}>
                  {cat.action}
                </span>
              </Link>
            );
            })}
          </div>
        </div>
      </section>

      {/* ========== THE GRACELAND DISPLAY ========== */}
      <section 
        style={{ 
          backgroundColor: '#EEE3D0',
          padding: 'clamp(40px, 6vw, 72px) clamp(16px, 4vw, 48px)'
        }}
        data-testid="graceland-section"
      >
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 350px), 1fr))',
            gap: 'clamp(24px, 4vw, 48px)', 
            alignItems: 'center' 
          }}>
            {/* Image */}
            <div style={{
              background: 'rgba(255, 252, 245, 0.92)',
              border: '1px solid rgba(181, 138, 60, 0.30)',
              padding: 'clamp(8px, 2vw, 16px)',
              borderRadius: '4px',
              order: window.innerWidth < 768 ? 2 : 1
            }}>
              <img 
                src={COLLECTION_IMAGES.gracelandDisplay}
                alt="The Aloha from Hawaii ticket collection displayed at Graceland"
                style={{
                  display: 'block',
                  width: '100%',
                  height: 'auto',
                  maxHeight: '420px',
                  objectFit: 'cover',
                  objectPosition: 'center',
                  borderRadius: '2px'
                }}
                loading="lazy"
              />
              <p style={{
                marginTop: '10px',
                fontFamily: "'Source Sans 3', sans-serif",
                fontSize: '11px',
                color: '#6B7280',
                textAlign: 'center'
              }}>
                Documented display at Graceland on September 14, 2022.
              </p>
            </div>
            
            {/* Text */}
            <div style={{ order: window.innerWidth < 768 ? 1 : 2 }}>
              <p style={{ fontFamily: "'Source Sans 3', sans-serif", fontSize: '12px', fontWeight: 700, letterSpacing: '0.15em', textTransform: 'uppercase', color: '#8A6928', marginBottom: '12px' }}>
                The Collection at Graceland
              </p>
              <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(24px, 5vw, 36px)', color: '#0D273C', marginBottom: '16px', fontWeight: 400, lineHeight: 1.15 }}>
                Preserving the Documented History
              </h2>
              <p style={{ fontFamily: "'Source Serif 4', serif", fontSize: 'clamp(14px, 2vw, 16px)', lineHeight: 1.7, color: '#3D403F', marginBottom: '20px' }}>
                The Elvis Aloha Archive preserves one of the most significant collections of 
                original Aloha from Hawaii concert materials, including tickets, production 
                documents, and artifacts that document this historic event.
              </p>
              <p style={{ fontFamily: "'Source Serif 4', serif", fontSize: 'clamp(14px, 2vw, 16px)', lineHeight: 1.7, color: '#3D403F', marginBottom: '24px' }}>
                These materials have been documented at Graceland and other archives, 
                establishing their authenticity and historical significance.
              </p>
              <Link 
                to="/collection"
                style={{ fontFamily: "'Source Sans 3', sans-serif", fontSize: '13px', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#D85C4A', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '8px' }}
              >
                Explore the Full Collection <ArrowRight style={{ width: '16px', height: '16px' }} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ========== COLLECTION HIGHLIGHTS ========== */}
      <section 
        style={{ 
          backgroundColor: '#F7F1E5',
          padding: 'clamp(40px, 6vw, 72px) clamp(16px, 4vw, 48px)',
          borderTop: '1px solid rgba(181, 138, 60, 0.35)'
        }}
        data-testid="collection-highlights-section"
      >
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 'clamp(24px, 4vw, 40px)' }}>
            <p style={{ fontFamily: "'Source Sans 3', sans-serif", fontSize: '12px', fontWeight: 700, letterSpacing: '0.15em', textTransform: 'uppercase', color: '#8A6928', marginBottom: '8px' }}>
              Beyond the Tickets
            </p>
            <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(24px, 5vw, 36px)', color: '#0D273C', fontWeight: 400 }}>
              Collection Highlights
            </h2>
          </div>
          
          {/* 3-image grid */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 250px), 1fr))',
            gap: 'clamp(12px, 2vw, 24px)'
          }}>
            {[
              { 
                src: COLLECTION_IMAGES.documents, 
                title: 'Production Documents',
                desc: 'Original correspondence, contracts, and planning materials',
                link: '/collection?category=documents'
              },
              { 
                src: COLLECTION_IMAGES.artworkProofs, 
                title: 'Artwork Proofs',
                desc: 'Printing proofs and color tests from Globe Tickets',
                link: '/collection?category=artifacts'
              },
              { 
                src: COLLECTION_IMAGES.ticketDetail, 
                title: 'Ticket Details',
                desc: 'Close examination of printing and design variations',
                link: '/collection?category=tickets'
              }
            ].map((item, idx) => (
              <Link 
                key={idx}
                to={item.link}
                style={{
                  display: 'block',
                  textDecoration: 'none',
                  background: 'rgba(255, 252, 245, 0.88)',
                  border: '1px solid rgba(181, 138, 60, 0.30)',
                  borderRadius: '4px',
                  overflow: 'hidden',
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease'
                }}
              >
                <div style={{
                  position: 'relative',
                  paddingTop: '75%',
                  overflow: 'hidden'
                }}>
                  <img 
                    src={item.src}
                    alt={item.title}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      objectPosition: 'center'
                    }}
                    loading="lazy"
                  />
                </div>
                <div style={{ padding: 'clamp(12px, 2vw, 20px)' }}>
                  <h3 style={{ 
                    fontFamily: "'Source Sans 3', sans-serif", 
                    fontSize: '14px', 
                    fontWeight: 700, 
                    color: '#0D273C',
                    marginBottom: '6px'
                  }}>
                    {item.title}
                  </h3>
                  <p style={{ 
                    fontFamily: "'Source Sans 3', sans-serif", 
                    fontSize: '12px', 
                    color: '#6B7280',
                    lineHeight: 1.5
                  }}>
                    {item.desc}
                  </p>
                </div>
              </Link>
            ))}
          </div>
          
          <p style={{
            marginTop: 'clamp(16px, 2vw, 24px)',
            fontFamily: "'Source Sans 3', sans-serif",
            fontSize: '11px',
            color: '#6B7280',
            textAlign: 'center'
          }}>
            Documented display at Graceland on September 14, 2022.
          </p>
        </div>
      </section>

      {/* ========== FEATURED ARTIFACT ========== */}
      <section 
        style={{ 
          backgroundColor: '#EEE3D0',
          padding: 'clamp(40px, 6vw, 72px) clamp(16px, 4vw, 48px)'
        }}
        data-testid="featured-section"
      >
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 350px), 1fr))',
            gap: 'clamp(24px, 4vw, 48px)', 
            alignItems: 'start' 
          }}>
            {/* Text */}
            <div>
              <p style={{ fontFamily: "'Source Sans 3', sans-serif", fontSize: '12px', fontWeight: 700, letterSpacing: '0.15em', textTransform: 'uppercase', color: '#8A6928', marginBottom: '12px' }}>
                Featured Artifact
              </p>
              <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(24px, 5vw, 32px)', color: '#0D273C', marginBottom: '16px', fontWeight: 400 }}>
                {featured?.title || "Original Concert Ticket Stub"}
              </h2>
              <p style={{ fontFamily: "'Source Serif 4', serif", fontSize: 'clamp(14px, 2vw, 16px)', lineHeight: 1.7, color: '#3D403F', marginBottom: '20px' }}>
                Issued for the live audience at the Honolulu International Center Arena 
                on January 14, 1973. This original ticket represents one of the most 
                sought-after artifacts from the historic Aloha from Hawaii concert.
              </p>
              <Link 
                to={featured ? `/collection/item/${featured.slug}` : "/collection"}
                style={{ fontFamily: "'Source Sans 3', sans-serif", fontSize: '13px', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#D85C4A', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '8px' }}
              >
                View This Artifact <ArrowRight style={{ width: '16px', height: '16px' }} />
              </Link>
            </div>
            
            {/* Artifact Image */}
            <div style={{ backgroundColor: '#F7F1E5', borderRadius: '4px', padding: 'clamp(16px, 3vw, 32px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {featured?.images?.[0]?.url ? (
                <img 
                  src={featured.images[0].url} 
                  alt={featured.title}
                  style={{ maxHeight: '280px', maxWidth: '100%', objectFit: 'contain', boxShadow: '0 4px 20px rgba(0,0,0,0.1)', border: '6px solid white' }}
                />
              ) : (
                <div style={{ backgroundColor: 'white', border: '1px solid #D8CDBD', padding: 'clamp(16px, 3vw, 32px)', textAlign: 'center', maxWidth: '280px', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
                  <p style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.1em', color: '#598085', marginBottom: '4px' }}>Aloha from Hawaii</p>
                  <p style={{ fontSize: '9px', fontWeight: 700, color: '#598085', marginBottom: '10px' }}>VIA SATELLITE</p>
                  <div style={{ borderTop: '1px solid #D8CDBD', borderBottom: '1px solid #D8CDBD', padding: '10px 0', marginBottom: '10px' }}>
                    <p style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(18px, 4vw, 22px)', fontWeight: 700, color: '#0D273C' }}>ELVIS PRESLEY</p>
                    <p style={{ fontSize: '10px', color: '#5A4133', marginTop: '4px' }}>HONOLULU INTERNATIONAL CENTER ARENA</p>
                  </div>
                  <p style={{ fontSize: '12px', fontWeight: 600, color: '#D85C4A' }}>SUN. • JAN. 14, 1973</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ========== TIMELINE PREVIEW ========== */}
      <section 
        style={{ 
          backgroundColor: '#F7F1E5',
          padding: 'clamp(32px, 5vw, 56px) clamp(16px, 4vw, 48px)',
          borderTop: '1px solid rgba(181, 138, 60, 0.45)'
        }}
        data-testid="timeline-section"
      >
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 'clamp(24px, 4vw, 40px)' }}>
            <p style={{ fontFamily: "'Source Sans 3', sans-serif", fontSize: '12px', fontWeight: 700, letterSpacing: '0.15em', textTransform: 'uppercase', color: '#8A6928', marginBottom: '8px' }}>
              A Night to Remember
            </p>
            <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(24px, 5vw, 36px)', color: '#0D273C', fontWeight: 400 }}>
              Timeline of Events
            </h2>
          </div>
          
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))',
            gap: 'clamp(12px, 2vw, 24px)'
          }}>
            {[
              { date: "1972", title: "Planning" },
              { date: "Jan 12", title: "Dress Rehearsal" },
              { date: "Jan 14", title: "Satellite Concert" },
              { date: "Feb 4", title: "$77K Proceeds" },
              { date: "Apr 4", title: "US Broadcast" },
            ].map((entry, idx) => (
              <div key={idx} style={{ textAlign: 'center' }}>
                <div style={{ width: 'clamp(36px, 8vw, 52px)', height: 'clamp(36px, 8vw, 52px)', margin: '0 auto 10px', backgroundColor: '#EEE3D0', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <span style={{ color: '#8A6928', fontSize: '14px' }}>◆</span>
                </div>
                <p style={{ fontFamily: "'Source Sans 3', sans-serif", fontSize: '10px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#D85C4A', marginBottom: '4px' }}>
                  {entry.date}
                </p>
                <h3 style={{ fontFamily: "'Source Sans 3', sans-serif", fontSize: 'clamp(11px, 2vw, 13px)', fontWeight: 700, color: '#0D273C' }}>
                  {entry.title}
                </h3>
              </div>
            ))}
          </div>
          
          <div style={{ textAlign: 'center', marginTop: 'clamp(20px, 3vw, 32px)' }}>
            <Link to="/event/timeline" style={{ fontFamily: "'Source Sans 3', sans-serif", fontSize: '13px', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#D85C4A', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
              View Full Timeline <ArrowRight style={{ width: '16px', height: '16px' }} />
            </Link>
          </div>
        </div>
      </section>

      {/* ========== ARCHIVE STANDARDS ========== */}
      {/* ========== ARCHIVE METHODOLOGY (BB-067 Batch 1: Source-backed content) ========== */}
      <section 
        style={{ 
          backgroundColor: '#EEE3D0',
          padding: 'clamp(32px, 4vw, 48px) clamp(16px, 4vw, 48px)'
        }}
        data-testid="standards-section"
      >
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 'clamp(16px, 3vw, 28px)' }}>
            <p style={{ fontFamily: "'Source Sans 3', sans-serif", fontSize: '11px', fontWeight: 700, letterSpacing: '0.15em', textTransform: 'uppercase', color: '#8A6928', marginBottom: '6px' }}>
              Evidence-Driven Research
            </p>
            <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(22px, 4vw, 28px)', color: '#0D273C', fontWeight: 400 }}>
              Archive Methodology
            </h2>
          </div>
          
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 180px), 1fr))',
            gap: 'clamp(10px, 2vw, 14px)',
            marginBottom: 'clamp(16px, 2.5vw, 24px)'
          }}>
            {[
              { iconType: 'file-text', title: 'Tiered Sources', desc: 'Contemporaneous 1972-73 evidence takes precedence over later materials' },
              { iconType: 'search', title: 'Cross-Referenced', desc: 'Claims verified against primary newspaper, broadcast, and artifact records' },
              { iconType: 'book-open', title: 'Documented Gaps', desc: 'Open questions acknowledged rather than filled with speculation' },
              { iconType: 'help-circle', title: 'Owner Verification', desc: 'Physical details require direct confirmation before stating as fact' },
            ].map((std, idx) => {
              const methodIcons = {
                'file-text': <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><line x1="10" y1="9" x2="8" y2="9"/></svg>,
                'search': <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></svg>,
                'book-open': <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg>,
                'help-circle': <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><path d="M12 17h.01"/></svg>
              };
              return (
              <div key={idx} style={{ backgroundColor: 'white', border: '1px solid rgba(181, 138, 60, 0.32)', borderRadius: '4px', padding: 'clamp(12px, 2vw, 16px)', textAlign: 'center' }}>
                <div style={{ marginBottom: '8px', color: '#0D273C', display: 'flex', justifyContent: 'center' }}>{methodIcons[std.iconType]}</div>
                <h3 style={{ fontFamily: "'Source Sans 3', sans-serif", fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#0D273C', marginBottom: '4px' }}>
                  {std.title}
                </h3>
                <p style={{ fontFamily: "'Source Sans 3', sans-serif", fontSize: '10px', lineHeight: 1.4, color: '#6B7280' }}>
                  {std.desc}
                </p>
              </div>
            );
            })}
          </div>
          
          <div style={{ textAlign: 'center' }}>
            <Link to="/governance/methodology" style={{ fontFamily: "'Source Sans 3', sans-serif", fontSize: '12px', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#D85C4A', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
              View Full Methodology <ArrowRight style={{ width: '14px', height: '14px' }} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
